import { actionProgress, currentWeek, TASK_STATUS_LABEL, suggestStatus, type TaskStatus } from '@supply/contracts'
import { Check, Lightbulb, Pencil, Plus, Trash2, X } from 'lucide-react'
import * as React from 'react'

import { DatePicker, quickTime, TimePicker } from '@/components/date-picker'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { Progress } from '@/components/ui/progress'
import { createAction, deleteAction, updateAction } from '@/data/api'
import type { Task, TaskAction } from '@/data/types'
import { invalidate } from '@/data/use-load'
import { formatDateShort } from '@/lib/format'
import { useMemberName } from '@/lib/session'
import { useWrite } from '@/lib/use-write'
import { cn } from '@/lib/utils'

import { ActionTextarea } from './task-create/AutoTextarea'

/**
 * Checklist da tarefa ("Ações a serem tomadas"). As ações SUGEREM o status —
 * quem pode mudar o status aceita a sugestão com um clique (no protótipo elas
 * recalculavam o status sozinhas).
 */
export function TaskActionsPanel({
  task,
  actions,
  canEdit,
  canChangeStatus,
  onApplyStatus,
}: {
  task: Task
  actions: TaskAction[]
  canEdit: boolean
  canChangeStatus: boolean
  onApplyStatus: (s: TaskStatus) => void
}) {
  const { run, pending } = useWrite()
  const name = useMemberName()
  const progress = actionProgress(actions)
  const suggestion = suggestStatus(task.status, progress)
  const [draft, setDraft] = React.useState({ texto: '', due_date: '', due_time: '' })
  const newTimeRef = React.useRef<HTMLInputElement>(null)
  const [editing, setEditing] = React.useState<string | null>(null)
  const { confirm, confirmDialog } = useConfirm()

  async function add(e: React.FormEvent) {
    e.preventDefault()
    if (!draft.texto.trim()) return
    const last = actions.at(-1)?.position ?? 0
    const r = await run(
      createAction({
        task_id: task.id,
        area_id: task.area_id,
        semana: currentWeek(),
        texto: draft.texto.trim(),
        due_date: draft.due_date || null,
        due_time: draft.due_time || null,
        position: last + 1000,
      }),
    )
    if (r.ok) {
      setDraft({ texto: '', due_date: '', due_time: '' })
      invalidate()
    }
  }

  async function toggle(a: TaskAction, done: boolean) {
    const r = await run(updateAction(a.id, { done }))
    if (r.ok) invalidate()
  }

  async function remove(a: TaskAction) {
    if (!(await confirm({ title: 'Excluir ação?', description: a.texto, confirmLabel: 'Excluir', destructive: true }))) return
    const r = await run(deleteAction(a.id), { success: 'Ação excluída' })
    if (r.ok) invalidate()
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Progress value={progress.pct} aria-label="Progresso das ações" />
        <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
          {progress.done}/{progress.total} · {progress.pct}%
        </span>
      </div>

      {suggestion && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-info/40 bg-info/5 px-3 py-2 text-sm">
          <Lightbulb className="h-4 w-4 text-info" aria-hidden />
          <span className="flex-1">
            {progress.done} de {progress.total} ações concluídas — sugestão: <b>{TASK_STATUS_LABEL[suggestion]}</b>.
          </span>
          {canChangeStatus && (
            <Button size="sm" variant="outline" onClick={() => onApplyStatus(suggestion)}>
              Aplicar
            </Button>
          )}
        </div>
      )}

      <ul className="divide-y divide-border rounded-md border border-border">
        {actions.map((a) =>
          editing === a.id ? (
            <ActionEditor
              key={a.id}
              action={a}
              onCancel={() => setEditing(null)}
              onSave={async (patch) => {
                const r = await run(updateAction(a.id, patch), { success: 'Ação atualizada' })
                if (r.ok) {
                  setEditing(null)
                  invalidate()
                }
              }}
            />
          ) : (
            <li key={a.id} className="flex items-start gap-3 px-3 py-2.5">
              <Checkbox
                checked={a.done}
                disabled={!canEdit || pending}
                onCheckedChange={(v) => void toggle(a, v === true)}
                aria-label={a.done ? `Desmarcar: ${a.texto}` : `Concluir: ${a.texto}`}
                className="mt-0.5"
              />
              <div className="min-w-0 flex-1">
                <p className={cn('whitespace-pre-wrap break-words text-sm', a.done && 'text-muted-foreground line-through')}>{a.texto}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {a.due_date && (
                    <span className="font-mono tabular-nums">
                      {formatDateShort(a.due_date)}
                      {a.due_time ? ` às ${a.due_time.slice(0, 5)}` : ''}
                    </span>
                  )}
                  {a.done && a.done_by && (
                    <span>
                      {a.due_date ? ' · ' : ''}concluída por {name(a.done_by)}
                    </span>
                  )}
                </p>
              </div>
              {canEdit && (
                <div className="flex shrink-0 gap-1">
                  <Button variant="ghost" size="icon-sm" aria-label="Editar ação" onClick={() => setEditing(a.id)}>
                    <Pencil />
                  </Button>
                  <Button variant="ghost" size="icon-sm" aria-label="Excluir ação" onClick={() => void remove(a)}>
                    <Trash2 />
                  </Button>
                </div>
              )}
            </li>
          ),
        )}
        {!actions.length && <li className="px-3 py-6 text-center text-sm text-muted-foreground">Nenhuma ação ainda.</li>}
      </ul>

      {canEdit && (
        <form onSubmit={(e) => void add(e)} className="grid grid-cols-2 items-start gap-2 sm:grid-cols-[1fr_150px_110px_auto]">
          <ActionTextarea
            className="col-span-2 sm:col-span-1"
            aria-label="Nova ação"
            placeholder="Nova ação a ser tomada"
            value={draft.texto}
            onChange={(e) => setDraft((d) => ({ ...d, texto: e.target.value }))}
          />
          <DatePicker aria-label="Data da nova ação" thenFocus={() => newTimeRef.current} placeholder="Data" value={draft.due_date} onChange={(due_date) => setDraft((d) => ({ ...d, due_date, due_time: quickTime(due_date, d.due_time) }))} />
          <TimePicker ref={newTimeRef} aria-label="Hora da nova ação" value={draft.due_time} onChange={(due_time) => setDraft((d) => ({ ...d, due_time }))} />
          <Button type="submit" className="col-span-2 sm:col-span-1" disabled={pending || !draft.texto.trim()}>
            <Plus /> Adicionar
          </Button>
        </form>
      )}
      {confirmDialog}
    </div>
  )
}

function ActionEditor({
  action,
  onSave,
  onCancel,
}: {
  action: TaskAction
  onSave: (patch: { texto: string; due_date: string | null; due_time: string | null }) => Promise<void>
  onCancel: () => void
}) {
  const timeRef = React.useRef<HTMLInputElement>(null)
  const [v, setV] = React.useState({
    texto: action.texto,
    due_date: action.due_date ?? '',
    due_time: action.due_time?.slice(0, 5) ?? '',
  })
  return (
    <li className="px-3 py-2.5">
      <form
        className="grid grid-cols-2 items-start gap-2 sm:grid-cols-[1fr_150px_110px_auto]"
        onSubmit={(e) => {
          e.preventDefault()
          if (v.texto.trim()) void onSave({ texto: v.texto.trim(), due_date: v.due_date || null, due_time: v.due_time || null })
        }}
      >
        <ActionTextarea className="col-span-2 sm:col-span-1" aria-label="Texto da ação" value={v.texto} onChange={(e) => setV({ ...v, texto: e.target.value })} autoFocus />
        <DatePicker aria-label="Data" placeholder="Data" thenFocus={() => timeRef.current} value={v.due_date} onChange={(due_date) => setV({ ...v, due_date, due_time: quickTime(due_date, v.due_time) })} />
        <TimePicker ref={timeRef} aria-label="Hora" value={v.due_time} onChange={(due_time) => setV({ ...v, due_time })} />
        <div className="col-span-2 flex gap-1 sm:col-span-1">
          <Button type="submit" size="icon" aria-label="Salvar ação">
            <Check />
          </Button>
          <Button type="button" size="icon" variant="ghost" aria-label="Cancelar edição" onClick={onCancel}>
            <X />
          </Button>
        </div>
      </form>
    </li>
  )
}
